import { useEffect, useMemo, useRef, useState } from "react";
import { useConnection, useSignMessage } from "wagmi";
import { AUTH_CHAIN_ID, CONTROL_ORIGIN, createWalletAuthClient, readWalletAuthCapabilities,
  WalletAuthorizationError, WalletOperationGuard, type WalletAuthCapabilities, type WalletAuthClient,
  type WalletOperation, type WalletSessionDescriptor } from "./wallet-auth";
import type { DeploymentReference } from "./public-v2";

let pendingWalletLogout: Promise<void> | null = null;
const SIGN_IN_ATTEMPT_TIMEOUT_MS = 5 * 60_000;
const SIGN_IN_TIMEOUT_MESSAGE = "Wallet sign-in timed out. Dismiss the pending wallet request, then try again.";

const awaitSignInStep = <T,>(signal: AbortSignal, pending: Promise<T>): Promise<T> => {
  // Wallet providers may reject after our deadline. Attach a handler before racing so that
  // a stale provider rejection never becomes an unhandled promise rejection.
  void pending.catch(() => undefined);
  if (signal.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (kind: "resolve" | "reject", value: unknown) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      if (kind === "reject") reject(value);
      else resolve(value as T);
    };
    const onAbort = () => finish("reject", new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    pending.then((value) => finish("resolve", value), (error) => finish("reject", error));
  });
};

export interface WalletSessionUiOptions {
  readonly deployment: DeploymentReference | null;
  readonly access: "authentication" | "private_reads";
}

export interface WalletSessionUi {
  readonly connection: Readonly<{
    address: `0x${string}` | undefined;
    chainId: number | undefined;
    status: "connected" | "connecting" | "reconnecting" | "disconnected";
  }>;
  readonly capabilities: WalletAuthCapabilities | null;
  readonly session: WalletSessionDescriptor | null;
  readonly client: WalletAuthClient;
  readonly signingIn: boolean;
  readonly logoutPending: boolean;
  readonly error: string | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  clearSession(): void;
  beginOperation(): WalletOperation;
  refreshSession(operation: WalletOperation): Promise<void>;
}

export function useWalletSession({ deployment, access }: WalletSessionUiOptions): WalletSessionUi {
  const { address, chainId, status } = useConnection();
  const { signMessageAsync, isPending: signing, reset: resetSignMessage } = useSignMessage();
  const [capabilityState, setCapabilityState] = useState<{ deploymentKey: string; value: WalletAuthCapabilities } | null>(null);
  const [session, setSession] = useState<WalletSessionDescriptor | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [logoutPending, setLogoutPending] = useState(!!pendingWalletLogout);
  const [errorState, setError] = useState<{ identityKey: string; message: string } | null>(null);
  const guard = useRef(new WalletOperationGuard()).current;
  const mounted = useRef(true);
  const signMessageResetRef = useRef(resetSignMessage);
  signMessageResetRef.current = resetSignMessage;
  const activeSignInRef = useRef<{
    operation: WalletOperation;
    identity: string;
    deadlineAt: number;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const deploymentKey = JSON.stringify(deployment);
  const currentCapabilities = capabilityState?.deploymentKey === deploymentKey ? capabilityState.value : null;
  const authorized = !!deployment && currentCapabilities?.walletAuthentication === true &&
    (access === "authentication" || currentCapabilities.privateAgentReads === true);
  const client = useMemo(() => createWalletAuthClient({ enabled: authorized, deployment }), [authorized, deployment]);
  const connection = { address, chainId, status } as WalletSessionUi["connection"];
  const capabilityIdentity = currentCapabilities ? JSON.stringify(currentCapabilities) : null;
  const identityKey = JSON.stringify({ status, address: address?.toLowerCase() ?? null, chainId: chainId ?? null,
    deploymentKey, access, authorized, capabilityIdentity });
  const activeIdentityKey = useRef(identityKey);
  activeIdentityKey.current = identityKey;
  const activeDeploymentKey = useRef(deploymentKey);
  activeDeploymentKey.current = deploymentKey;
  const previousIdentityKey = useRef(identityKey);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const clientRef = useRef(client);
  clientRef.current = client;

  const setCurrentSession = (value: WalletSessionDescriptor | null) => {
    sessionRef.current = value;
    if (mounted.current) setSession(value);
  };

  const resetSignMessageMutation = () => {
    try { signMessageResetRef.current(); } catch { /* resetting mutation state cannot replace auth cleanup */ }
  };

  const cancelActiveSignIn = () => {
    const attempt = activeSignInRef.current;
    if (!attempt) return;
    activeSignInRef.current = null;
    clearTimeout(attempt.timer);
    guard.invalidate();
    attempt.operation.finish();
    resetSignMessageMutation();
    if (mounted.current) setSigningIn(false);
  };

  const expireSignInAttempt = (attempt: NonNullable<typeof activeSignInRef.current>) => {
    if (activeSignInRef.current !== attempt) return;
    activeSignInRef.current = null;
    clearTimeout(attempt.timer);
    guard.invalidate();
    attempt.operation.finish();
    resetSignMessageMutation();
    if (mounted.current && activeIdentityKey.current === attempt.identity) {
      setSigningIn(false);
      setError({ identityKey: attempt.identity, message: SIGN_IN_TIMEOUT_MESSAGE });
    }
  };

  const signInAttemptIsCurrent = (attempt: NonNullable<typeof activeSignInRef.current>): boolean => {
    if (activeSignInRef.current !== attempt || !mounted.current || activeIdentityKey.current !== attempt.identity ||
        !attempt.operation.isCurrent()) return false;
    if (Date.now() >= attempt.deadlineAt) {
      expireSignInAttempt(attempt);
      return false;
    }
    return true;
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelActiveSignIn();
      guard.invalidate();
    };
  }, [guard]);

  useEffect(() => {
    if (!deployment) {
      setCapabilityState(null);
      return;
    }
    const controller = new AbortController();
    const expectedKey = deploymentKey;
    void readWalletAuthCapabilities({ demo: false, publicControlOrigin: CONTROL_ORIGIN,
      publicDeployment: deployment, signal: controller.signal })
      .then((value) => {
        if (!controller.signal.aborted && activeDeploymentKey.current === expectedKey && mounted.current) {
          setCapabilityState({ deploymentKey: expectedKey, value });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted && mounted.current) {
          setCapabilityState({ deploymentKey: expectedKey, value: {
            deployment: null, walletAuthentication: false, privateAgentControls: false, privateAgentReads: false,
            admission: false, payments: false, configuredTierIds: [],
          } });
        }
      });
    return () => controller.abort();
  }, [deployment, deploymentKey]);

  useEffect(() => {
    const pending = pendingWalletLogout;
    if (!pending) return;
    setLogoutPending(true);
    void pending.then(() => {
      if (mounted.current && !pendingWalletLogout) setLogoutPending(false);
    }, () => {
      if (mounted.current && !pendingWalletLogout) setLogoutPending(false);
    });
  }, []);

  useEffect(() => {
    if (previousIdentityKey.current === identityKey) return () => { cancelActiveSignIn(); guard.invalidate(); };
    previousIdentityKey.current = identityKey;
    cancelActiveSignIn();
    guard.invalidate();
    setCurrentSession(null);
    setError(null);
    setSigningIn(false);
    return () => { cancelActiveSignIn(); guard.invalidate(); };
  }, [deployment, guard, identityKey]);

  useEffect(() => {
    if (!session) return;
    const expiresAt = Math.min(session.expiresAtMs, session.idleExpiresAtMs);
    const expectedIdentity = identityKey;
    const expectedSession = session;
    let timer: ReturnType<typeof setTimeout>;
    const expireIfDue = () => {
      if (!mounted.current || activeIdentityKey.current !== expectedIdentity || sessionRef.current !== expectedSession) return;
      const remaining = expiresAt - Date.now();
      if (remaining > 0) {
        timer = setTimeout(expireIfDue, Math.min(remaining, 2_147_000_000));
        return;
      }
      guard.invalidate();
      setCurrentSession(null);
      setError({ identityKey: expectedIdentity, message: "Your wallet session expired. Sign in again." });
      setSigningIn(false);
    };
    timer = setTimeout(expireIfDue, Math.min(Math.max(0, expiresAt - Date.now()), 2_147_000_000));
    return () => clearTimeout(timer);
  }, [guard, identityKey, session]);

  const beginOperation = (): WalletOperation => {
    cancelActiveSignIn();
    const expectedIdentity = activeIdentityKey.current;
    const operation = guard.begin();
    return {
      signal: operation.signal,
      isCurrent: () => operation.isCurrent() && mounted.current && activeIdentityKey.current === expectedIdentity,
      finish: () => operation.finish(),
    };
  };

  const clearSession = () => {
    cancelActiveSignIn();
    guard.invalidate();
    setCurrentSession(null);
    if (mounted.current) {
      setError(null);
      setSigningIn(false);
    }
  };

  const signIn = async () => {
    if (pendingWalletLogout || logoutPending || signingIn || signing || !authorized || !address ||
        status !== "connected" || chainId !== AUTH_CHAIN_ID) return;
    const operation = beginOperation();
    const requestedIdentity = activeIdentityKey.current;
    const deadlineAt = Date.now() + SIGN_IN_ATTEMPT_TIMEOUT_MS;
    const attempt: NonNullable<typeof activeSignInRef.current> = {
      operation, identity: requestedIdentity, deadlineAt,
      timer: setTimeout(() => expireSignInAttempt(attempt), SIGN_IN_ATTEMPT_TIMEOUT_MS),
    };
    activeSignInRef.current = attempt;
    setSigningIn(true);
    setError(null);
    try {
      const challenge = await awaitSignInStep(operation.signal, clientRef.current.challenge(address, operation.signal));
      if (!signInAttemptIsCurrent(attempt)) return;
      const signature = await awaitSignInStep(operation.signal, signMessageAsync({ message: challenge.message }));
      if (!signInAttemptIsCurrent(attempt)) return;
      const descriptor = await awaitSignInStep(operation.signal,
        clientRef.current.verify(address, challenge.message, signature, challenge.preauthCsrfToken, operation.signal));
      if (signInAttemptIsCurrent(attempt) &&
          descriptor.address.toLowerCase() === address.toLowerCase() && descriptor.chainId === AUTH_CHAIN_ID &&
          Math.min(descriptor.expiresAtMs, descriptor.idleExpiresAtMs) > Date.now()) {
        setCurrentSession(descriptor);
      }
    } catch (cause) {
      if (activeSignInRef.current === attempt && Date.now() >= attempt.deadlineAt) {
        expireSignInAttempt(attempt);
        return;
      }
      if (activeSignInRef.current === attempt && operation.isCurrent() && activeIdentityKey.current === requestedIdentity && mounted.current) {
        setError({ identityKey: requestedIdentity, message: cause instanceof WalletAuthorizationError ? cause.message : "Wallet sign-in failed. Try again." });
      }
    } finally {
      if (activeSignInRef.current === attempt) {
        activeSignInRef.current = null;
        clearTimeout(attempt.timer);
        if (mounted.current && activeIdentityKey.current === requestedIdentity) setSigningIn(false);
        operation.finish();
        resetSignMessageMutation();
      }
    }
  };

  const signOut = async () => {
    const oldSession = sessionRef.current;
    clearSession();
    if (!oldSession || pendingWalletLogout) return;
    if (mounted.current) setLogoutPending(true);
    const request = createWalletAuthClient({ enabled: true, deployment }).logout(oldSession.csrfToken, AbortSignal.timeout(1500)).catch(() => undefined);
    const barrier = request.then(() => undefined, () => undefined).then(() => {
      if (pendingWalletLogout === barrier) pendingWalletLogout = null;
      if (mounted.current) setLogoutPending(false);
    });
    pendingWalletLogout = barrier;
    await barrier;
  };

  const refreshSession = async (operation: WalletOperation) => {
    const captured = sessionRef.current;
    const expectedIdentity = activeIdentityKey.current;
    const capturedClient = clientRef.current;
    if (!captured || !operation.isCurrent() || captured.chainId !== AUTH_CHAIN_ID ||
        !address || captured.address.toLowerCase() !== address.toLowerCase() || chainId !== AUTH_CHAIN_ID ||
        Math.min(captured.expiresAtMs, captured.idleExpiresAtMs) <= Date.now()) {
      throw new WalletAuthorizationError("expired");
    }
    const refreshed = await capturedClient.session(captured.address, captured.csrfToken, operation.signal);
    if (!operation.isCurrent() || activeIdentityKey.current !== expectedIdentity || sessionRef.current !== captured ||
        Math.min(captured.expiresAtMs, captured.idleExpiresAtMs) <= Date.now() ||
        refreshed.address.toLowerCase() !== captured.address.toLowerCase() || refreshed.chainId !== AUTH_CHAIN_ID ||
        Math.min(refreshed.expiresAtMs, refreshed.idleExpiresAtMs) <= Date.now()) return;
    setCurrentSession(refreshed);
  };

  const visibleSession = session && authorized && status === "connected" && address && chainId === AUTH_CHAIN_ID &&
    session.address.toLowerCase() === address.toLowerCase() && Math.min(session.expiresAtMs, session.idleExpiresAtMs) > Date.now()
    ? session : null;

  return { connection, capabilities: currentCapabilities, session: visibleSession, client, signingIn: signingIn || signing,
    logoutPending: logoutPending || !!pendingWalletLogout,
    error: errorState?.identityKey === identityKey ? errorState.message : null,
    signIn, signOut, clearSession, beginOperation, refreshSession };
}
